/**
 * Gestionnaire de Modules - Service Central
 * 
 * Gère l'activation/désactivation des modules de manière sécurisée
 * Évite les plantages lors de la désactivation des modules
 */

import { useState, useEffect } from 'react';

export interface ModuleConfig {
  id: string;
  name: string;
  active: boolean;
  dependencies: string[];
  component?: React.ComponentType<any>;
  required?: boolean; // Modules critiques qui ne peuvent pas être désactivés
}

export interface ModuleState {
  [moduleId: string]: {
    active: boolean;
    loaded: boolean;
    error?: string;
  };
}

class ModuleManagerService {
  private modules: Map<string, ModuleConfig> = new Map();
  private moduleStates: ModuleState = {};
  private listeners: Set<(state: ModuleState) => void> = new Set();

  constructor() {
    this.loadModuleStates();
  }

  /**
   * Enregistrer un module
   */
  registerModule(config: ModuleConfig): void {
    this.modules.set(config.id, config);
    
    if (!this.moduleStates[config.id]) {
      this.moduleStates[config.id] = {
        active: config.active,
        loaded: false
      };
    }
    
    this.saveModuleStates();
    this.notifyListeners();
  }

  /**
   * Obtenir tous les modules enregistrés
   */
  getAllModules(): ModuleConfig[] {
    return Array.from(this.modules.values());
  }

  /**
   * Obtenir l'état d'un module
   */
  getModuleState(moduleId: string): { active: boolean; loaded: boolean; error?: string } | null {
    return this.moduleStates[moduleId] || null;
  }

  /**
   * Vérifier si un module est actif
   */
  isModuleActive(moduleId: string): boolean {
    const state = this.moduleStates[moduleId];
    return state ? state.active : false;
  }

  /**
   * Activer un module
   */
  async activateModule(moduleId: string): Promise<boolean> {
    const module = this.modules.get(moduleId);
    if (!module) {
      console.error(`Module ${moduleId} non trouvé`);
      return false;
    }

    try {
      // Vérifier et activer les dépendances d'abord
      for (const depId of module.dependencies) {
        if (!this.isModuleActive(depId)) {
          const activated = await this.activateModule(depId);
          if (!activated) {
            throw new Error(`Impossible d'activer la dépendance ${depId}`);
          }
        }
      }

      // Activer le module
      this.moduleStates[moduleId] = {
        ...this.moduleStates[moduleId],
        active: true,
        loaded: true,
        error: undefined
      };

      this.saveModuleStates();
      this.notifyListeners();
      
      console.log(`✅ Module ${moduleId} activé avec succès`);
      return true;

    } catch (error) {
      this.moduleStates[moduleId] = {
        ...this.moduleStates[moduleId],
        active: false,
        loaded: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };

      this.saveModuleStates();
      this.notifyListeners();
      
      console.error(`❌ Erreur activation module ${moduleId}:`, error);
      return false;
    }
  }

  /**
   * Désactiver un module
   */
  async deactivateModule(moduleId: string): Promise<boolean> {
    const module = this.modules.get(moduleId);
    if (!module) {
      console.error(`Module ${moduleId} non trouvé`);
      return false;
    }

    // Vérifier si le module est requis
    if (module.required) {
      console.warn(`⚠️ Impossible de désactiver le module requis ${moduleId}`);
      return false;
    }

    try {
      // Vérifier les dépendances - désactiver les modules qui dépendent de celui-ci
      const dependentModules = this.getDependentModules(moduleId);
      
      if (dependentModules.length > 0) {
        const moduleNames = dependentModules.map(m => m.name).join(', ');
        const shouldContinue = window.confirm(
          `Les modules suivants dépendent de ${module.name} et seront également désactivés :\n${moduleNames}\n\nContinuer ?`
        );
        
        if (!shouldContinue) {
          return false;
        }

        // Désactiver les modules dépendants d'abord
        for (const depModule of dependentModules) {
          await this.deactivateModule(depModule.id);
        }
      }

      // Désactiver le module
      this.moduleStates[moduleId] = {
        ...this.moduleStates[moduleId],
        active: false,
        loaded: false,
        error: undefined
      };

      this.saveModuleStates();
      this.notifyListeners();
      
      console.log(`🔴 Module ${moduleId} désactivé avec succès`);
      return true;

    } catch (error) {
      this.moduleStates[moduleId] = {
        ...this.moduleStates[moduleId],
        error: error instanceof Error ? error.message : 'Erreur lors de la désactivation'
      };

      this.saveModuleStates();
      this.notifyListeners();
      
      console.error(`❌ Erreur désactivation module ${moduleId}:`, error);
      return false;
    }
  }

  /**
   * Basculer l'état d'un module
   */
  async toggleModule(moduleId: string): Promise<boolean> {
    const isActive = this.isModuleActive(moduleId);
    
    if (isActive) {
      return await this.deactivateModule(moduleId);
    } else {
      return await this.activateModule(moduleId);
    }
  }

  /**
   * Obtenir les modules qui dépendent d'un module donné
   */
  private getDependentModules(moduleId: string): ModuleConfig[] {
    return Array.from(this.modules.values()).filter(module => 
      module.dependencies.includes(moduleId) && this.isModuleActive(module.id)
    );
  }

  /**
   * Sauvegarder l'état des modules dans localStorage
   */
  private saveModuleStates(): void {
    try {
      localStorage.setItem('module-states', JSON.stringify(this.moduleStates));
    } catch (error) {
      console.error('Erreur sauvegarde états modules:', error);
    }
  }

  /**
   * Charger l'état des modules depuis localStorage
   */
  private loadModuleStates(): void {
    try {
      const saved = localStorage.getItem('module-states');
      if (saved) {
        this.moduleStates = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Erreur chargement états modules:', error);
      this.moduleStates = {};
    }
  }

  /**
   * Ajouter un listener pour les changements d'état
   */
  addStateListener(listener: (state: ModuleState) => void): void {
    this.listeners.add(listener);
  }

  /**
   * Supprimer un listener
   */
  removeStateListener(listener: (state: ModuleState) => void): void {
    this.listeners.delete(listener);
  }

  /**
   * Notifier tous les listeners des changements d'état
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener({ ...this.moduleStates });
      } catch (error) {
        console.error('Erreur notification listener:', error);
      }
    });
  }

  /**
   * Réinitialiser tous les modules à leur état par défaut
   */
  resetAllModules(): void {
    this.modules.forEach(module => {
      this.moduleStates[module.id] = {
        active: module.active,
        loaded: false,
        error: undefined
      };
    });
    
    this.saveModuleStates();
    this.notifyListeners();
    
    console.log('🔄 Tous les modules ont été réinitialisés');
  }

  /**
   * Obtenir les statistiques des modules
   */
  getModuleStats(): {
    total: number;
    active: number;
    inactive: number;
    errors: number;
  } {
    const total = this.modules.size;
    let active = 0;
    let inactive = 0;
    let errors = 0;

    Object.values(this.moduleStates).forEach(state => {
      if (state.error) {
        errors++;
      } else if (state.active) {
        active++;
      } else {
        inactive++;
      }
    });

    return { total, active, inactive, errors };
  }
}

// Instance singleton
export const moduleManager = new ModuleManagerService();

// Hook React pour utiliser le gestionnaire de modules
export function useModuleManager() {
  const [moduleStates, setModuleStates] = useState<ModuleState>(moduleManager['moduleStates']);

  useEffect(() => {
    const listener = (newState: ModuleState) => {
      setModuleStates(newState);
    };

    moduleManager.addStateListener(listener);
    
    return () => {
      moduleManager.removeStateListener(listener);
    };
  }, []);

  return {
    moduleStates,
    modules: moduleManager.getAllModules(),
    isModuleActive: (id: string) => moduleManager.isModuleActive(id),
    toggleModule: (id: string) => moduleManager.toggleModule(id),
    activateModule: (id: string) => moduleManager.activateModule(id),
    deactivateModule: (id: string) => moduleManager.deactivateModule(id),
    registerModule: (config: ModuleConfig) => moduleManager.registerModule(config),
    getModuleStats: () => moduleManager.getModuleStats(),
    resetAllModules: () => moduleManager.resetAllModules()
  };
}